import { useState, useEffect, useRef, useCallback } from 'react';
import type { AiAvatarMode } from './AiInterviewAvatar';
import { AI_INTERVIEW_CONSENT_VERSION } from './interviewPrepConstants';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@core/auth/useAuth';
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Check,
  CheckCircle,
  ChevronDown,
  Clock,
  Headphones,
  Loader2,
  Lock,
  LogOut,
  MessageCircle,
  Mic,
  PhoneOff,
  RefreshCw,
  Settings,
  Sparkles,
  Video,
  XCircle,
} from 'lucide-react';
import { isPlausiblePublicToken, fetchWithTimeout } from '@core/utils/publicToken';
import { getInterviewPublicEdgeUrl, interviewEdgeConfigErrorMessage } from '@core/utils/interviewEnv';
import { interviewEdgeUndeployedMessage, parseInterviewEdgeJson } from '@core/utils/interviewEdgeResponse';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';
import { Modal } from '@shared/components/ui/Modal';
import { AI_INTERVIEW_LANGUAGES, AI_INTERVIEW_SESSION_TYPES, interviewTargetMinutesForSessionType, sessionInterviewStepLabel } from './interviewPrepConstants';
import { InterviewPublicAppBar } from './InterviewPublicAppBar';
import { AiInterviewExperienceModal } from './AiInterviewExperienceModal';
import {
  defaultVoiceForLanguage,
  isKnownElevenLabsVoiceId,
  voicesForInterviewLanguage,
} from './elevenLabsVoices';
import { AiInterviewAgentBar } from './AiInterviewAgentBar';
import { AiInterviewVoiceMic } from './AiInterviewVoiceMic';
import { postInterviewExperienceFeedback } from './publicInterviewApi';

type PageState = 'loading' | 'ready' | 'error' | 'chat' | 'done';

type StoredMsg = { role: 'user' | 'assistant'; content: string; at: string };

function publicInterviewHeaders(): Record<string, string> {
  return {
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
  };
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Browser speech synthesis BCP-47 tag from interview language preference. */
function speechLangForInterview(code: string): string {
  const c = (code || 'en').toLowerCase();
  if (c === 'hi-en' || c.includes('hinglish')) return 'en-IN';
  if (c === 'hi-in' || (c.startsWith('hi') && !c.includes('hinglish'))) return 'hi-IN';
  if (c.startsWith('kn')) return 'kn-IN';
  if (c.startsWith('mr')) return 'mr-IN';
  if (c.startsWith('ru')) return 'ru-RU';
  if (c.startsWith('pt')) return 'pt-BR';
  if (c.startsWith('es')) return 'es-ES';
  if (c.startsWith('en-in')) return 'en-IN';
  if (c.startsWith('en')) return 'en-US';
  return 'en-US';
}

export function AiInterviewPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  /** Supabase org row — authoritative for hub permissions. */
  const isOrgRecruiter = Boolean(user?.orgId);
  /** Anyone signed into the B2B app (Keycloak) should leave the public link for pipeline + feedback, even if org hydration lags. */
  const isInternalAppUser = isOrgRecruiter || isAuthenticated;
  /** When opened as a recruiter popup, close window and notify parent on completion instead of navigating. */
  const isPopupMode = searchParams.get('popup') === '1';
  const postInterviewNavigatedRef = useRef(false);
  const [state, setState] = useState<PageState>('loading');
  const [error, setError] = useState('');
  const [orgName, setOrgName] = useState('');
  const [jobTitle, setJobTitle] = useState<string | null>(null);
  const [language, setLanguage] = useState('');
  const [sessionType, setSessionType] = useState('screening');
  const [sessionStatus, setSessionStatus] = useState('');
  const [sessionIdDisplay, setSessionIdDisplay] = useState('');
  /** Full UUID for redirects (e.g. AI interview session detail + feedback). */
  const [sessionRowId, setSessionRowId] = useState('');
  const [messages, setMessages] = useState<StoredMsg[]>([]);
  const [consentAt, setConsentAt] = useState<string | null>(null);
  const [permAt, setPermAt] = useState<string | null>(null);
  const [prefsConfirmedAt, setPrefsConfirmedAt] = useState<string | null>(null);
  const [interviewStartedAt, setInterviewStartedAt] = useState<string | null>(null);
  const [completedAt, setCompletedAt] = useState<string | null>(null);
  const [completedTooShort, setCompletedTooShort] = useState(false);
  const [retakesRemaining, setRetakesRemaining] = useState(3);
  const [minDurationSeconds, setMinDurationSeconds] = useState(120);
  const [recordingStoragePath, setRecordingStoragePath] = useState<string | null>(null);
  const [candidateFirstName, setCandidateFirstName] = useState('');
  const [ttsVoiceId, setTtsVoiceId] = useState(() => defaultVoiceForLanguage('en-IN'));

  const [sending, setSending] = useState(false);
  const [openingBusy, setOpeningBusy] = useState(false);
  const [voiceListening, setVoiceListening] = useState(false);
  const [consentBusy, setConsentBusy] = useState(false);
  const [permBusy, setPermBusy] = useState(false);
  const [completeBusy, setCompleteBusy] = useState(false);
  const [retakeBusy, setRetakeBusy] = useState(false);
  const [avatarMode, setAvatarMode] = useState<AiAvatarMode>('idle');
  const [clockNow, setClockNow] = useState(() => Date.now());
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showRetakeConfirm, setShowRetakeConfirm] = useState(false);
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [guideAccordionOpen, setGuideAccordionOpen] = useState(true);
  const [transcriptAccordionOpen, setTranscriptAccordionOpen] = useState(true);
  const [recordSessionKey, setRecordSessionKey] = useState(0);
  const [experienceSubmittedAt, setExperienceSubmittedAt] = useState<string | null>(null);
  const [showExperienceModal, setShowExperienceModal] = useState(false);
  const experienceDismissedRef = useRef(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const speakClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const interviewEdgeUrl = getInterviewPublicEdgeUrl();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, state]);

  useEffect(() => {
    return () => {
      if (speakClearRef.current) clearTimeout(speakClearRef.current);
      mediaRecorderRef.current?.stop();
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  useEffect(() => {
    if (!interviewStartedAt || (state !== 'chat' && state !== 'done')) return;
    const t = setInterval(() => setClockNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [interviewStartedAt, state]);

  const elapsedSeconds =
    interviewStartedAt != null
      ? Math.max(0, Math.floor((clockNow - new Date(interviewStartedAt).getTime()) / 1000))
      : 0;

  const maxLabel = formatElapsed(minDurationSeconds);
  const guideTargetMinutes = interviewTargetMinutesForSessionType(sessionType);
  const guideTargetSeconds = guideTargetMinutes * 60;
  const guideProgressPct = Math.min(100, (elapsedSeconds / Math.max(1, guideTargetSeconds)) * 100);

  useEffect(() => {
    if (!token) {
      setState('error');
      setError('Missing link token.');
      return;
    }
    if (!isPlausiblePublicToken(token)) {
      setState('error');
      setError('This link does not look valid. Ask your recruiter for a fresh interview link.');
      return;
    }

    const cfgErr = interviewEdgeConfigErrorMessage();
    if (cfgErr) {
      setState('error');
      setError(cfgErr);
      return;
    }
    if (!interviewEdgeUrl) {
      setState('error');
      setError(interviewEdgeConfigErrorMessage() ?? 'Interview API URL is not configured.');
      return;
    }

    let cancelled = false;
    setState('loading');
    setError('');

    fetchWithTimeout(`${interviewEdgeUrl}?token=${encodeURIComponent(token)}&mode=info`, {
      headers: publicInterviewHeaders(),
      timeoutMs: 30_000,
    })
      .then(async (r) => {
        const text = await r.text();
        const parsed = parseInterviewEdgeJson(text);
        if (!parsed.ok) {
          if (cancelled) return;
          setState('error');
          setError(
            import.meta.env.DEV
              ? interviewEdgeUndeployedMessage(r.status)
              : 'Interview service is unavailable. Your recruiting team needs to deploy the AI interview function on Supabase.',
          );
          return;
        }
        const data = parsed.data as {
          error?: string;
          org_name?: string;
          candidate_first_name?: string;
          tts_voice_id?: string | null;
          job_title?: string | null;
          language?: string;
          session_type?: string;
          session_id?: string;
          session_id_display?: string;
          status?: string;
          messages?: StoredMsg[];
          consent_at?: string | null;
          permissions_granted_at?: string | null;
          completed_at?: string | null;
          prefs_confirmed_at?: string | null;
          interview_started_at?: string | null;
          completed_too_short?: boolean;
          retakes_remaining?: number;
          min_duration_seconds?: number;
          recording_storage_path?: string | null;
          candidate_experience_submitted_at?: string | null;
        };
        if (cancelled) return;
        if (r.status === 410) {
          setState('error');
          setError(data.error ?? 'This link has expired.');
          return;
        }
        if (!r.ok || data.error) {
          setState('error');
          setError(data.error ?? 'Could not load this interview.');
          return;
        }
        setOrgName(data.org_name ?? '—');
        setJobTitle(data.job_title ?? null);
        setCandidateFirstName((data.candidate_first_name ?? '').trim());
        const voiceFromServer =
          typeof data.tts_voice_id === 'string' && data.tts_voice_id.length > 0 ? data.tts_voice_id : '';
        const voiceStorageKey = token ? `ggj_interview_tts_voice_${token}` : '';
        const voiceFromStorage = voiceStorageKey ? localStorage.getItem(voiceStorageKey) ?? '' : '';
        const langResolved = data.language ?? 'en-IN';
        const list = voicesForInterviewLanguage(langResolved);
        const resolvedVoice =
          (voiceFromServer && list.some((v) => v.id === voiceFromServer) && voiceFromServer) ||
          (voiceFromStorage && list.some((v) => v.id === voiceFromStorage) ? voiceFromStorage : '') ||
          defaultVoiceForLanguage(langResolved);
        setTtsVoiceId(isKnownElevenLabsVoiceId(resolvedVoice) ? resolvedVoice : defaultVoiceForLanguage(langResolved));
        setLanguage(langResolved);
        setSessionType(data.session_type ?? 'screening');
        setSessionIdDisplay(data.session_id_display ?? '');
        setSessionRowId(typeof data.session_id === 'string' ? data.session_id : '');
        setSessionStatus(data.status ?? '');
        setMessages(Array.isArray(data.messages) ? data.messages : []);
        setConsentAt(data.consent_at ?? null);
        setPermAt(data.permissions_granted_at ?? null);
        setPrefsConfirmedAt(data.prefs_confirmed_at ?? null);
        setInterviewStartedAt(data.interview_started_at ?? null);
        setCompletedAt(data.completed_at ?? null);
        setCompletedTooShort(Boolean(data.completed_too_short));
        setRetakesRemaining(data.retakes_remaining ?? 3);
        setMinDurationSeconds(data.min_duration_seconds ?? 120);
        setRecordingStoragePath(data.recording_storage_path ?? null);
        setExperienceSubmittedAt(data.candidate_experience_submitted_at ?? null);

        if (data.status === 'completed' || data.status === 'cancelled') {
          setState('done');
          return;
        }
        setState('ready');
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setState('error');
          const aborted = e instanceof Error && e.name === 'AbortError';
          const msg = e instanceof Error ? e.message : String(e);
          setError(
            aborted
              ? 'Request timed out. Check your connection and try again.'
              : import.meta.env.DEV
                ? `Network error: ${msg}. If this persists, run: npm run deploy:ai-interview (with Supabase CLI logged in).`
                : 'Network error. Check your connection and try again.',
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, interviewEdgeUrl]);

  function notifyPopupDone(sid: string) {
    try {
      window.opener?.postMessage({ type: 'ggj_interview_done', sessionRowId: sid }, window.location.origin);
    } catch {
      /* opener may be gone */
    }
    window.close();
  }

  useEffect(() => {
    if (state !== 'done' || completedTooShort) return;
    if (experienceSubmittedAt) return;
    if (experienceDismissedRef.current) return;
    if (token && sessionStorage.getItem(`ggj_interview_feedback_dismissed_${token}`)) return;
    if (authLoading) return;

    // Popup mode (recruiter-launched window): always show feedback modal first,
    // then close popup after the candidate rates or dismisses.
    if (isPopupMode) {
      setShowExperienceModal(true);
      return;
    }

    // Signed-in B2B user on regular public link → go to session hub for rating.
    if (isInternalAppUser && sessionRowId && token) {
      try {
        sessionStorage.setItem(`ggj_interview_feedback_token_${sessionRowId}`, token);
      } catch {
        /* ignore */
      }
      postInterviewNavigatedRef.current = true;
      window.location.replace(`${window.location.origin}/ai-interviews/${sessionRowId}?experience=1`);
      return;
    }
    if (isInternalAppUser) return;
    setShowExperienceModal(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, completedTooShort, experienceSubmittedAt, token, isInternalAppUser, sessionRowId, authLoading, isPopupMode]);

  useEffect(() => {
    if (!token || !consentAt || !permAt) return;
    if (prefsConfirmedAt || messages.length > 0) return;
    if (sessionStatus === 'completed' || sessionStatus === 'cancelled') return;
    navigate(`/interview/configure/${token}`, { replace: true });
  }, [token, consentAt, permAt, prefsConfirmedAt, messages.length, sessionStatus, navigate]);

  const needsConsent = !consentAt;
  const needsPermissions = consentAt && !permAt;
  const canChat = Boolean(
    consentAt && permAt && prefsConfirmedAt && sessionStatus !== 'completed' && sessionStatus !== 'cancelled',
  );

  /** After feedback modal (submit/skip/close), send org users back to the AI interviews hub. */
  const resolvePostInterviewDestination = useCallback((): string | null => {
    const raw = searchParams.get('returnTo')?.trim();
    if (raw && /^\/ai-interviews(\/.*)?$/i.test(raw)) return raw;
    if (isInternalAppUser && sessionRowId) return `/ai-interviews/${sessionRowId}`;
    if (isInternalAppUser) return '/ai-interviews';
    return null;
  }, [searchParams, isInternalAppUser, sessionRowId]);

  const handleExperienceModalClose = useCallback(() => {
    experienceDismissedRef.current = true;
    if (token) {
      try {
        sessionStorage.setItem(`ggj_interview_feedback_dismissed_${token}`, '1');
      } catch {
        /* ignore */
      }
    }
    setShowExperienceModal(false);
    if (postInterviewNavigatedRef.current) return;
    // Popup mode: close the popup and notify the parent window.
    if (isPopupMode) {
      postInterviewNavigatedRef.current = true;
      notifyPopupDone(sessionRowId);
      return;
    }
    const dest = resolvePostInterviewDestination();
    if (dest) {
      postInterviewNavigatedRef.current = true;
      navigate(dest, { replace: true });
    }
  }, [token, navigate, resolvePostInterviewDestination, isPopupMode, sessionRowId]);

  const startCameraAndRecording = useCallback(() => {
    if (!canChat || state !== 'chat') return;
    void (async () => {
      try {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        recordedChunksRef.current = [];
        const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
          ? 'video/webm;codecs=vp8,opus'
          : 'video/webm';
        const rec = new MediaRecorder(stream, { mimeType: mime });
        mediaRecorderRef.current = rec;
        rec.ondataavailable = (e) => {
          if (e.data.size > 0) recordedChunksRef.current.push(e.data);
        };
        rec.start(1000);
      } catch {
        /* preview optional */
      }
    })();
  }, [canChat, state]);

  useEffect(() => {
    if (state === 'chat' && canChat) startCameraAndRecording();
  }, [state, canChat, startCameraAndRecording, recordSessionKey]);

  async function postBody(body: Record<string, unknown>) {
    const url = getInterviewPublicEdgeUrl();
    if (!url) {
      return {
        r: new Response(null, { status: 503 }),
        data: { error: interviewEdgeConfigErrorMessage() ?? 'Not configured' } as Record<string, unknown> & { error?: string },
      };
    }
    const r = await fetchWithTimeout(url, {
      method: 'POST',
      headers: { ...publicInterviewHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, ...body }),
      timeoutMs: 120_000,
    });
    const text = await r.text();
    const parsed = parseInterviewEdgeJson(text);
    const data = (
      parsed.ok ? parsed.data : { error: interviewEdgeUndeployedMessage(r.status) }
    ) as Record<string, unknown> & {
      error?: string;
      messages?: StoredMsg[];
      status?: string;
      ok?: boolean;
    };
    return { r, data };
  }

  async function submitExperienceRating(rating: number) {
    if (!token) throw new Error('Missing session token.');
    await postInterviewExperienceFeedback(token, rating);
    setExperienceSubmittedAt(new Date().toISOString());
    // Popup mode: modal will show thank-you state, then close on dismiss.
    if (isPopupMode) return;
    // Candidates stay on the thank-you page; signed-in app users leave the public URL after rating.
    if (isInternalAppUser && sessionRowId) {
      postInterviewNavigatedRef.current = true;
      window.location.replace(`${window.location.origin}/ai-interviews/${sessionRowId}`);
      return;
    }
    if (isInternalAppUser) {
      postInterviewNavigatedRef.current = true;
      window.location.replace(`${window.location.origin}/ai-interviews`);
    }
  }

  function playSpeechFallback(text: string) {
    if (!('speechSynthesis' in window)) {
      speakClearRef.current = setTimeout(() => {
        setAvatarMode('idle');
        speakClearRef.current = null;
      }, Math.min(12000, 800 + text.length * 40));
      return;
    }
    window.speechSynthesis.cancel();
    const chunk = text.slice(0, 8000);
    const u = new SpeechSynthesisUtterance(chunk);
    u.lang = speechLangForInterview(language);
    u.rate = 0.98;
    u.onend = () => {
      setAvatarMode('idle');
    };
    u.onerror = () => {
      setAvatarMode('idle');
    };
    window.speechSynthesis.speak(u);
  }

  async function playAssistantTts(text: string) {
    if (speakClearRef.current) {
      clearTimeout(speakClearRef.current);
      speakClearRef.current = null;
    }
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setAvatarMode('speaking');
    try {
      const { r, data } = await postBody({ action: 'tts', tts_text: text, tts_voice_id: ttsVoiceId });
      if (r.status === 503 || (data as { tts_unavailable?: boolean }).tts_unavailable) {
        playSpeechFallback(text);
        return;
      }
      const b64 = typeof data.audio_base64 === 'string' ? data.audio_base64 : '';
      if (r.ok && b64.length > 0) {
        const bin = atob(b64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const blob = new Blob([bytes], { type: 'audio/mpeg' });
        const url = URL.createObjectURL(blob);
        const audio = new Audio();
        audioRef.current = audio;
        audio.src = url;
        audio.preload = 'auto';
        audio.volume = 1;
        audio.playsInline = true;
        const cleanup = () => {
          URL.revokeObjectURL(url);
          if (audioRef.current === audio) audioRef.current = null;
        };
        audio.onended = () => {
          cleanup();
          setAvatarMode('idle');
        };
        audio.onerror = () => {
          cleanup();
          playSpeechFallback(text);
        };
        try {
          await audio.play();
        } catch {
          cleanup();
          playSpeechFallback(text);
        }
        return;
      }
    } catch {
      /* fall through to speech */
    }
    playSpeechFallback(text);
  }

  async function uploadRecordingIfAny() {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state === 'inactive') return;
    await new Promise<void>((resolve) => {
      rec.onstop = () => resolve();
      rec.stop();
    });
    const chunks = recordedChunksRef.current;
    if (chunks.length === 0) return;
    const blob = new Blob(chunks, { type: 'video/webm' });
    if (blob.size < 1024) return;

    const { r, data } = await postBody({ action: 'recording_signed_upload' });
    const signedUrl = typeof data.signed_url === 'string' ? data.signed_url : '';
    const path = typeof data.path === 'string' ? data.path : '';
    if (!r.ok || !signedUrl || !path) return;

    const put = await fetch(signedUrl, {
      method: 'PUT',
      body: blob,
      headers: { 'Content-Type': 'video/webm' },
    });
    if (!put.ok) return;
    await postBody({ action: 'recording_finalize', storage_path: path });
  }

  async function onAcceptConsent() {
    if (!token) return;
    setConsentBusy(true);
    setError('');
    try {
      const { r, data } = await postBody({ action: 'consent', consent_version: AI_INTERVIEW_CONSENT_VERSION });
      if (!r.ok || data.error) {
        setError(data.error ?? 'Could not save consent.');
        return;
      }
      setConsentAt(new Date().toISOString());
    } finally {
      setConsentBusy(false);
    }
  }

  async function onGrantMedia() {
    if (!token) return;
    setPermBusy(true);
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      for (const t of stream.getTracks()) t.stop();
      const { r, data } = await postBody({ action: 'permissions' });
      if (!r.ok || data.error) {
        setError(data.error ?? 'Could not record permission.');
        return;
      }
      setPermAt(new Date().toISOString());
      setState('ready');
    } catch {
      setError(
        'Camera and microphone access was denied or unavailable. Allow access in your browser settings to continue.',
      );
    } finally {
      setPermBusy(false);
    }
  }

  useEffect(() => {
    if (state === 'ready' && consentAt && permAt && prefsConfirmedAt && sessionStatus !== 'completed') {
      setState('chat');
    }
  }, [state, consentAt, permAt, prefsConfirmedAt, sessionStatus]);

  async function startOpening() {
    if (!token || !canChat || openingBusy || sending) return;
    setOpeningBusy(true);
    setError('');
    setAvatarMode('thinking');
    try {
      const { r, data } = await postBody({ action: 'opening' });
      if (r.status === 403 && data.error === 'prefs_required') {
        navigate(`/interview/configure/${token}`);
        return;
      }
      if (!r.ok || data.error) {
        setError((data.error as string) ?? 'Could not start the interviewer.');
        setAvatarMode('idle');
        return;
      }
      if (Array.isArray(data.messages)) setMessages(data.messages as StoredMsg[]);
      if (data.status) setSessionStatus(data.status as string);
      const msgs = data.messages as StoredMsg[] | undefined;
      const lastAssistant = msgs?.filter((m) => m.role === 'assistant').pop();
      if (lastAssistant?.content) await playAssistantTts(lastAssistant.content);
      else setAvatarMode('idle');
    } finally {
      setOpeningBusy(false);
    }
  }

  async function sendUserMessage(text: string) {
    if (!token || !canChat || sending) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    setVoiceListening(false);
    setAvatarMode('thinking');
    setSending(true);
    setError('');
    try {
      const { r, data } = await postBody({ action: 'message', user_message: trimmed });
      if (r.status === 403 && data.error === 'consent_required') {
        setError('Please accept the consent terms first.');
        setAvatarMode('idle');
        return;
      }
      if (r.status === 403 && data.error === 'permissions_required') {
        setError('Please allow camera and microphone first.');
        setAvatarMode('idle');
        return;
      }
      if (r.status === 403 && data.error === 'prefs_required') {
        navigate(`/interview/configure/${token}`);
        return;
      }
      if (!r.ok || data.error) {
        setError((data.error as string) ?? 'The assistant could not reply. Try again.');
        setAvatarMode('idle');
        return;
      }
      if (Array.isArray(data.messages)) setMessages(data.messages);
      if (data.status) setSessionStatus(data.status as string);
      setState('chat');
      const msgs = data.messages as StoredMsg[] | undefined;
      const lastAssistant = msgs?.filter((m) => m.role === 'assistant').pop();
      if (lastAssistant?.content) void playAssistantTts(lastAssistant.content);
      else setAvatarMode('idle');
    } finally {
      setSending(false);
    }
  }

  async function onComplete() {
    if (!token) return;
    setCompleteBusy(true);
    setError('');
    // Close the first dialog immediately so the public interview UI does not stack modals / “done” under confirm.
    setShowEndConfirm(false);
    try {
      await uploadRecordingIfAny();
      const { r, data } = await postBody({ action: 'complete' });
      if (!r.ok && r.status !== 200) {
        setError((data.error as string) ?? 'Could not end the interview.');
        return;
      }
      setSessionStatus('completed');
      setCompletedAt(new Date().toISOString());
      const tooShort = Boolean(data.completed_too_short);
      setCompletedTooShort(tooShort);
      setAvatarMode('idle');
      if ('speechSynthesis' in window) window.speechSynthesis.cancel();
      // Logged-in B2B user: leave public URL before any “done” / feedback UI; rating only on /ai-interviews/:id.
      if (isInternalAppUser && sessionRowId && token) {
        if (!tooShort) {
          try {
            sessionStorage.setItem(`ggj_interview_feedback_token_${sessionRowId}`, token);
          } catch {
            /* ignore */
          }
        }
        // Popup mode: let setState('done') run so the useEffect shows the feedback modal.
        if (!isPopupMode) {
          postInterviewNavigatedRef.current = true;
          const q = tooShort ? '' : '?experience=1';
          window.location.replace(`${window.location.origin}/ai-interviews/${sessionRowId}${q}`);
          return;
        }
      }
      if (isInternalAppUser && token && !isPopupMode) {
        postInterviewNavigatedRef.current = true;
        window.location.replace(`${window.location.origin}/ai-interviews`);
        return;
      }
      setState('done');
    } finally {
      setCompleteBusy(false);
    }
  }

  async function onRetake() {
    if (!token) return;
    setRetakeBusy(true);
    setError('');
    try {
      if ('speechSynthesis' in window) window.speechSynthesis.cancel();
      audioRef.current?.pause();
      audioRef.current = null;
      mediaRecorderRef.current?.stop();
      streamRef.current?.getTracks().forEach((t) => t.stop());
      const { r, data } = await postBody({ action: 'retake' });
      if (!r.ok || data.error) {
        setError((data.error as string) ?? 'Could not start a retake.');
        return;
      }
      setMessages([]);
      setSessionStatus((data.status as string) ?? 'in_progress');
      setCompletedAt(null);
      setCompletedTooShort(false);
      setRetakesRemaining(Number(data.retakes_remaining ?? retakesRemaining));
      setInterviewStartedAt(new Date().toISOString());
      setRecordSessionKey((k) => k + 1);
      setState('chat');
      if (token) {
        try {
          sessionStorage.removeItem(`ggj_interview_feedback_dismissed_${token}`);
        } catch {
          /* ignore */
        }
      }
    } finally {
      setRetakeBusy(false);
      setShowRetakeConfirm(false);
    }
  }

  const stepLabel = sessionInterviewStepLabel(sessionType);
  const roundBadge =
    AI_INTERVIEW_SESSION_TYPES.find((t) => t.value === sessionType)?.label?.toUpperCase() ?? 'INTERVIEW';
  const languageLabel = AI_INTERVIEW_LANGUAGES.find((l) => l.value === language)?.label ?? language;
  const applicationTitle = jobTitle?.trim() || `${orgName} · AI interview`;
  const userMessageCount = messages.filter((m) => m.role === 'user').length;
  const assistantMessageCount = messages.filter((m) => m.role === 'assistant').length;

  if (state === 'loading') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-50 px-4 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600 dark:text-sky-400" aria-hidden />
        <p className="text-sm text-slate-600 dark:text-slate-400">Loading your interview…</p>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="mx-auto flex min-h-screen max-w-lg flex-col items-center justify-center gap-4 bg-slate-50 px-4 text-center text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <XCircle className="h-12 w-12 text-slate-400 dark:text-slate-500" aria-hidden />
        <h1 className="text-lg font-semibold">Interview unavailable</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">{error}</p>
      </div>
    );
  }

  const showGate = needsConsent || needsPermissions;

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 text-slate-900 transition-colors dark:bg-slate-950 dark:text-slate-100">
      {/* Candidates and popup-mode sessions show the in-page feedback modal. */}
      <AiInterviewExperienceModal
        open={showExperienceModal && !authLoading && (!isInternalAppUser || isPopupMode)}
        onClose={handleExperienceModalClose}
        onSubmit={(rating) => submitExperienceRating(rating)}
      />
      <Modal
        open={showEndConfirm}
        onClose={() => setShowEndConfirm(false)}
        title="End Interview"
        subtitle={`${stepLabel}${sessionIdDisplay ? ` · Session ${sessionIdDisplay}` : ''}`}
        icon={<PhoneOff className="w-4 h-4" aria-hidden />}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
            Are you ready to submit this interview? Your transcript and recording up to this point will be saved for your recruiter to review.
          </p>

          {retakesRemaining > 0 && (
            <div className="flex items-start gap-2.5 rounded-lg border border-amber-200/70 bg-amber-50/90 px-3 py-2.5 dark:border-amber-500/30 dark:bg-amber-950/30">
              <RefreshCw className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
              <p className="text-xs leading-snug text-amber-800 dark:text-amber-200">
                You have <strong>{retakesRemaining} retake{retakesRemaining === 1 ? '' : 's'}</strong> remaining — use them for genuine technical issues, not to restart.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              className="btn-secondary inline-flex items-center gap-1.5"
              disabled={completeBusy || retakesRemaining < 1 || retakeBusy}
              onClick={() => {
                setShowEndConfirm(false);
                setShowRetakeConfirm(true);
              }}
            >
              <RefreshCw className="w-3.5 h-3.5" aria-hidden />
              Retake
            </button>
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-1.5"
              disabled={completeBusy}
              onClick={() => {
                setShowEndConfirm(false);
                void onComplete();
              }}
            >
              {completeBusy
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
                : <LogOut className="w-3.5 h-3.5" aria-hidden />}
              Close
            </button>
          </div>
        </div>
      </Modal>
      <ConfirmDialog
        open={showRetakeConfirm}
        onClose={() => setShowRetakeConfirm(false)}
        onConfirm={() => void onRetake()}
        title="Start a new attempt?"
        message={`You have ${retakesRemaining} retake${retakesRemaining === 1 ? '' : 's'} remaining. Your current transcript will be cleared.`}
        confirmLabel="Retake"
        variant="primary"
        loading={retakeBusy}
        icon={<RefreshCw className="w-4 h-4" aria-hidden />}
      />

      <InterviewPublicAppBar
        title={showGate ? 'Interview access' : 'Guided interview'}
        subtitle={jobTitle ? `${orgName} · ${jobTitle}` : orgName}
        icon={<Headphones className="h-5 w-5" aria-hidden />}
        endExtras={
          !showGate && (state === 'chat' || state === 'done') ? (
            <div className="hidden items-center gap-2 sm:flex">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-500">Elapsed</span>
              <span className="font-mono text-sm tabular-nums text-slate-800 dark:text-slate-300">
                {formatElapsed(elapsedSeconds)}
                <span className="mx-1 text-slate-400 dark:text-slate-600">/</span>
                {maxLabel}
              </span>
            </div>
          ) : null
        }
      />

      {!showGate && (state === 'chat' || state === 'done') && (
        <div className="shrink-0 border-b border-violet-200/80 bg-gradient-to-b from-violet-50/90 to-slate-50/95 backdrop-blur-md dark:border-violet-900/35 dark:from-violet-950/40 dark:to-slate-950/80">
          <div
            className="h-1 w-full bg-[color:var(--ggj-logo-mark-bg,#8b80f9)]"
            aria-hidden
          />
          <div className="px-3 py-1.5">
          <AiInterviewAgentBar
            pageState={state}
            mode={avatarMode}
            sending={sending}
            userMessageCount={userMessageCount}
            assistantMessageCount={assistantMessageCount}
            assistantTurnIndex={assistantMessageCount}
          />
          </div>
        </div>
      )}

      {showGate && (
        <main className="mx-auto w-full max-w-lg flex-1 space-y-4 px-4 py-6">
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-500/35 dark:bg-amber-950/25 dark:text-amber-100/95">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>{error}</span>
            </div>
          )}

          {needsConsent && state === 'ready' && (
            <section className="space-y-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Consent</h2>
              <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-400">
                This session may use your camera and microphone for the interview experience. Audio may be processed by
                automated systems. By continuing, you agree to participate under your recruiter&apos;s terms and this
                recording notice (version {AI_INTERVIEW_CONSENT_VERSION}).
              </p>
              <button
                type="button"
                className="btn-primary text-sm px-4 py-2 inline-flex items-center gap-2 disabled:opacity-50"
                disabled={consentBusy}
                onClick={() => void onAcceptConsent()}
              >
                {consentBusy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : <CheckCircle className="w-4 h-4" />}
                I agree and continue
              </button>
            </section>
          )}

          {!needsConsent && needsPermissions && state === 'ready' && (
            <section className="space-y-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Browser permissions</h2>
              <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-400">
                We&apos;ll ask for camera and microphone access so your interview can run in the browser.
              </p>
              <button
                type="button"
                className="btn-primary text-sm px-4 py-2 inline-flex items-center gap-2 disabled:opacity-50"
                disabled={permBusy}
                onClick={() => void onGrantMedia()}
              >
                {permBusy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : <Video className="w-4 h-4" />}
                Allow camera &amp; microphone
              </button>
            </section>
          )}
        </main>
      )}

      {!showGate && (state === 'chat' || state === 'done') && (
        <div className="flex min-h-0 flex-1 flex-col">
          {/* Hidden video for MediaRecorder only — not shown in the immersive UI */}
          <video
            ref={videoRef}
            className="fixed w-px h-px opacity-0 pointer-events-none overflow-hidden"
            aria-hidden
            playsInline
            autoPlay
            muted
          />

          {!leftOpen && (
            <button
              type="button"
              className="fixed left-0 top-[38%] z-30 rounded-r-lg border border-l-0 border-slate-300 bg-white/95 py-3 pl-1 pr-2 text-slate-800 shadow-lg dark:border-slate-600 dark:bg-slate-800/95 dark:text-slate-200 lg:hidden"
              aria-label="Show progress"
              onClick={() => setLeftOpen(true)}
            >
              <ChevronRight className="w-4 h-4" aria-hidden />
            </button>
          )}
          {!rightOpen && (
            <button
              type="button"
              className="fixed right-0 top-[38%] z-30 rounded-l-lg border border-r-0 border-slate-300 bg-white/95 py-3 pl-2 pr-1 text-slate-800 shadow-lg dark:border-slate-600 dark:bg-slate-800/95 dark:text-slate-200 lg:hidden"
              aria-label="Show tips"
              onClick={() => setRightOpen(true)}
            >
              <ChevronLeft className="w-4 h-4" aria-hidden />
            </button>
          )}

          <div className="flex min-h-0 flex-1 flex-col gap-0 px-4 py-2 lg:flex-row">
            {/* Left panel — collapsible */}
            <div
              className={`flex shrink-0 flex-col overflow-hidden border-slate-200 transition-[width,max-width] duration-200 ease-out dark:border-slate-800 ${
                leftOpen ? 'w-full max-h-[40vh] border-b lg:max-h-none lg:w-[min(280px,28vw)] lg:border-b-0 lg:border-r' : 'w-0 max-w-0 border-0 lg:max-w-0'
              }`}
            >
              <aside className="min-w-[260px] space-y-4 overflow-y-auto p-3 lg:p-4 h-full">
                <div className="flex items-center justify-between gap-2 text-sm">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                    onClick={() => window.history.back()}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Go back
                  </button>
                  <button
                    type="button"
                    className="rounded p-1 text-slate-500 hover:bg-slate-200 dark:text-slate-400 dark:hover:bg-slate-800"
                    aria-label={leftOpen ? 'Collapse progress' : 'Expand progress'}
                    onClick={() => setLeftOpen(!leftOpen)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                </div>
                <div>
                  <h1 className="text-sm font-semibold leading-snug text-slate-900 dark:text-slate-100">
                    {applicationTitle}
                  </h1>
                  <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">Session ID: {sessionIdDisplay}</p>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                    <div className="h-full w-1/2 rounded-full bg-sky-600" />
                  </div>
                  {candidateFirstName ? (
                    <p className="mt-3 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
                      Personalized for{' '}
                      <strong className="font-medium text-slate-900 dark:text-slate-200">{candidateFirstName}</strong> —
                      your interviewer is briefed with your application context before you begin.
                    </p>
                  ) : (
                    <p className="mt-3 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
                      Your interviewer is briefed on your role and materials your recruiter attached to this session.
                    </p>
                  )}
                </div>
                <ol className="space-y-0 overflow-hidden rounded-lg border border-slate-200 bg-white text-sm dark:border-slate-700 dark:bg-slate-900/50">
                  <li className="flex items-start gap-2 border-b border-slate-200 px-3 py-2.5 dark:border-slate-800">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" aria-hidden />
                    <span className="text-slate-500 line-through dark:text-slate-500">Upload resume</span>
                  </li>
                  <li className="flex items-start gap-2 border-b border-slate-200 px-3 py-2.5 dark:border-slate-800">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" aria-hidden />
                    <span className="text-slate-500 line-through dark:text-slate-500">Work authorization</span>
                  </li>
                  <li className="flex items-start gap-2 border-b border-l-4 border-b-slate-200 border-l-sky-600 bg-sky-50/90 px-3 py-2.5 dark:border-b-slate-800 dark:border-l-sky-500 dark:bg-slate-800/40">
                    <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 border-sky-600 dark:border-sky-500" aria-hidden />
                    <div>
                      <span className="font-medium text-slate-900 dark:text-slate-100">{stepLabel}</span>
                      <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-800 dark:bg-slate-700 dark:text-slate-200">
                        {roundBadge}
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start gap-2 px-3 py-2.5 text-slate-600 dark:text-slate-500">
                    <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full border border-slate-400 dark:border-slate-600" aria-hidden />
                    <span>Follow-up</span>
                  </li>
                </ol>
                <p className="rounded-lg border border-slate-200 bg-slate-100 px-3 py-2.5 text-xs leading-snug text-slate-800 dark:border-slate-600 dark:bg-slate-800/70 dark:text-slate-200">
                  Language and AI voice were set on the configure step before you started. Current language:{' '}
                  <span className="font-semibold text-slate-950 dark:text-white">{languageLabel}</span>.
                </p>
              </aside>
            </div>

            <button
              type="button"
              className="hidden w-9 shrink-0 flex-col items-center justify-center border-r border-slate-200 bg-slate-100/90 text-slate-600 transition-colors hover:bg-slate-200/90 hover:text-slate-900 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-400 dark:hover:bg-slate-800/80 dark:hover:text-slate-200 lg:flex"
              aria-label={leftOpen ? 'Hide progress panel' : 'Show progress panel'}
              onClick={() => setLeftOpen(!leftOpen)}
            >
              {leftOpen ? <ChevronLeft className="h-5 w-5" aria-hidden /> : <ChevronRight className="h-5 w-5" aria-hidden />}
            </button>

            {/* Center — compact orb + conversation transcript + bottom dock */}
            <section className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
              {error && (
                <div className="flex items-start gap-2 rounded-md border border-amber-500/35 bg-amber-950/25 px-3 py-2 text-sm text-amber-100/95 dark:border-amber-500/30">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>{error}</span>
                </div>
              )}

              <div className="relative flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-md shadow-slate-900/5 dark:border-slate-600/50 dark:bg-slate-900 dark:shadow-black/20">
                {/* Top accent line */}
                <div className="h-1 w-full shrink-0 bg-[color:var(--ggj-logo-mark-bg,#8b80f9)]" aria-hidden />

                {/* AI Voice Orb */}
                <div className="shrink-0 flex flex-col items-center gap-2 pt-5 pb-4 border-b border-slate-100 dark:border-slate-800">
                  <style>{`
                    @keyframes ggj-orb-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                    @keyframes ggj-orb-spin-rev { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
                    @keyframes ggj-orb-breathe { 0%,100% { opacity:0.55; transform:scale(1); } 50% { opacity:0.9; transform:scale(1.07); } }
                  `}</style>
                  <div className="relative flex items-center justify-center" style={{ width: 96, height: 96 }}>
                    {/* Outer dashed rotating ring — always visible, slow */}
                    <div className="absolute rounded-full border border-dashed border-slate-300/50 dark:border-slate-600/40"
                      style={{ inset: 0, animation: 'ggj-orb-spin 18s linear infinite' }}
                    />
                    {/* Mid arc ring — speeds up when active */}
                    <div className="absolute rounded-full"
                      style={{
                        inset: 6,
                        border: '1.5px solid transparent',
                        backgroundClip: 'padding-box',
                        outline: `1.5px solid ${avatarMode === 'speaking' ? 'rgba(56,189,248,0.45)' : avatarMode === 'thinking' ? 'rgba(139,92,246,0.38)' : 'rgba(148,163,184,0.2)'}`,
                        outlineOffset: '-1.5px',
                        borderRadius: '50%',
                        animation: `ggj-orb-spin-rev ${avatarMode === 'speaking' ? '2.5s' : avatarMode === 'thinking' ? '5s' : '12s'} linear infinite`,
                      }}
                    />
                    {/* Conic gradient halo — rotates, intensity matches state */}
                    <div className="absolute rounded-full"
                      style={{
                        inset: 10,
                        background: avatarMode === 'speaking'
                          ? 'conic-gradient(from 0deg, rgba(56,189,248,0.55), rgba(99,102,241,0.15), rgba(56,189,248,0.55))'
                          : avatarMode === 'thinking'
                            ? 'conic-gradient(from 0deg, rgba(139,92,246,0.5), rgba(99,102,241,0.1), rgba(139,92,246,0.5))'
                            : 'conic-gradient(from 0deg, rgba(148,163,184,0.15), rgba(203,213,225,0.05), rgba(148,163,184,0.15))',
                        animation: `ggj-orb-spin ${avatarMode === 'speaking' ? '1.8s' : avatarMode === 'thinking' ? '4s' : '10s'} linear infinite`,
                        borderRadius: '50%',
                      }}
                    />
                    {/* Ping blooms when speaking */}
                    {avatarMode === 'speaking' && (
                      <>
                        <div className="absolute rounded-full bg-sky-400/12 animate-ping" style={{ inset: 12, animationDuration: '1s' }} />
                        <div className="absolute rounded-full bg-indigo-400/10 animate-ping" style={{ inset: 16, animationDuration: '1.4s', animationDelay: '0.35s' }} />
                      </>
                    )}
                    {/* Slow breathe when thinking */}
                    {avatarMode === 'thinking' && (
                      <div className="absolute rounded-full bg-violet-400/15"
                        style={{ inset: 14, animation: 'ggj-orb-breathe 2s ease-in-out infinite' }}
                      />
                    )}
                    {/* Centre gradient sphere */}
                    <div className="relative z-10 flex items-center justify-center rounded-full transition-all duration-500"
                      style={{
                        width: 52, height: 52,
                        background: avatarMode === 'speaking'
                          ? 'linear-gradient(135deg,#38bdf8 0%,#6366f1 55%,#8b5cf6 100%)'
                          : avatarMode === 'thinking'
                            ? 'linear-gradient(135deg,#7c3aed 0%,#6366f1 55%,#a78bfa 100%)'
                            : state === 'done'
                              ? 'linear-gradient(135deg,#10b981 0%,#059669 100%)'
                              : 'linear-gradient(135deg,#64748b 0%,#475569 100%)',
                        boxShadow: avatarMode === 'speaking'
                          ? '0 0 24px rgba(56,189,248,0.5),0 0 48px rgba(99,102,241,0.25)'
                          : avatarMode === 'thinking'
                            ? '0 0 20px rgba(124,58,237,0.45),0 0 40px rgba(99,102,241,0.2)'
                            : state === 'done'
                              ? '0 0 16px rgba(16,185,129,0.35)'
                              : '0 4px 14px rgba(0,0,0,0.2)',
                      }}
                    >
                      {/* shine overlay */}
                      <div className="absolute inset-0 rounded-full"
                        style={{ background: 'linear-gradient(135deg,rgba(255,255,255,0.28) 0%,transparent 55%)' }}
                      />
                      <Mic className="relative z-10 h-5 w-5 text-white drop-shadow" aria-hidden />
                    </div>
                  </div>
                  <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                    {avatarMode === 'speaking'
                      ? 'Interviewer is speaking\u2026'
                      : avatarMode === 'thinking'
                        ? 'Thinking\u2026'
                        : state === 'done'
                          ? 'Interview complete'
                          : messages.length === 0
                            ? 'Ready \u2014 tap Start conversation below'
                            : 'Listening for your response'}
                  </p>
                </div>

                {/* Conversation transcript */}
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden" aria-busy={sending}>
                  <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3">
                    {messages.length === 0 && state === 'chat' && (
                      <div className="flex items-center justify-center h-full min-h-[100px] text-center py-8">
                        <p className="text-sm text-slate-400 dark:text-slate-500">
                          The conversation will appear here once you start.
                        </p>
                      </div>
                    )}
                    {messages.map((m, i) => (
                      <div key={`${m.at}-${i}`} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div
                          className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap shadow-sm ${
                            m.role === 'user'
                              ? 'rounded-br-sm bg-sky-600 text-white dark:bg-sky-600'
                              : 'rounded-bl-sm border border-slate-200/80 bg-slate-50 text-slate-800 dark:border-slate-700/60 dark:bg-slate-800 dark:text-slate-100'
                          }`}
                        >
                          {m.content}
                        </div>
                      </div>
                    ))}

                    {state === 'done' && completedTooShort && (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 space-y-2 dark:border-amber-500/40 dark:bg-amber-950/30">
                        <p className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-100">
                          <AlertCircle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                          Interview too short
                        </p>
                        <p className="text-xs text-slate-700 dark:text-slate-300">
                          Your interview was shorter than the minimum time ({minDurationSeconds}s). You&apos;ll need to retake it to submit a complete attempt.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="btn-primary text-sm px-4 py-2 disabled:opacity-50"
                            disabled={retakesRemaining < 1 || retakeBusy}
                            onClick={() => setShowRetakeConfirm(true)}
                          >
                            Retake now
                          </button>
                          {isPopupMode && (
                            <button
                              type="button"
                              className="btn-secondary text-sm px-4 py-2"
                              onClick={() => notifyPopupDone(sessionRowId)}
                            >
                              Close
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {state === 'done' && !completedTooShort && (
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50/95 px-4 py-3 space-y-2 dark:border-emerald-500/35 dark:bg-emerald-950/45">
                        <p className="flex items-center gap-2 text-sm font-semibold text-emerald-900 dark:text-emerald-100">
                          <CheckCircle className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
                          Thank you
                        </p>
                        <p className="text-xs leading-relaxed text-slate-700 dark:text-slate-300">
                          {completedAt
                            ? 'This interview is closed. Your recruiting team may follow up with next steps.'
                            : 'This interview is closed.'}{' '}
                          {recordingStoragePath
                            ? 'Transcript and recording are saved for their review.'
                            : 'Conversation transcript is saved for their review.'}
                        </p>
                        {isAuthenticated && (
                          <Link
                            to={sessionRowId ? `/ai-interviews/${sessionRowId}` : '/ai-interviews'}
                            className="inline-flex items-center rounded-lg bg-emerald-700 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-emerald-800 dark:bg-emerald-600 dark:hover:bg-emerald-500"
                          >
                            {sessionRowId ? 'Open session details' : 'Open AI interviews'}
                          </Link>
                        )}
                      </div>
                    )}

                    <div ref={bottomRef} />
                  </div>
                </div>

                {/* Bottom dock */}
                <div className="shrink-0 border-t border-slate-100 bg-slate-50/90 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/80">
                  <div className="flex flex-col gap-2">
                    {state === 'chat' && messages.length === 0 && (
                      <div className="flex items-center justify-end">
                        <button
                          type="button"
                          title="Unlock microphone and play the interviewer's opening"
                          className="btn-primary inline-flex shrink-0 items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm disabled:opacity-50"
                          disabled={!canChat || openingBusy || sending}
                          onClick={() => void startOpening()}
                        >
                          {openingBusy
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                            : <Sparkles className="h-3.5 w-3.5" aria-hidden />
                          }
                          Start conversation
                        </button>
                      </div>
                    )}

                    {state === 'chat' && messages.length > 0 && (
                      <AiInterviewVoiceMic
                        disabled={!canChat || sending || openingBusy || avatarMode === 'speaking' || avatarMode === 'thinking'}
                        listening={voiceListening}
                        language={language}
                        onListeningChange={setVoiceListening}
                        onTranscript={(t) => void sendUserMessage(t)}
                      />
                    )}
                  </div>
                </div>
              </div>
            </section>

            <button
              type="button"
              className="hidden w-9 shrink-0 flex-col items-center justify-center border-l border-slate-200 bg-slate-100/90 text-slate-600 transition-colors hover:bg-slate-200/90 hover:text-slate-900 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-400 dark:hover:bg-slate-800/80 dark:hover:text-slate-200 lg:flex"
              aria-label={rightOpen ? 'Hide tips panel' : 'Show tips panel'}
              onClick={() => setRightOpen(!rightOpen)}
            >
              {rightOpen ? <ChevronRight className="h-5 w-5" aria-hidden /> : <ChevronLeft className="h-5 w-5" aria-hidden />}
            </button>

            <div
              className={`flex shrink-0 flex-col overflow-hidden border-slate-200 transition-[width,max-width] duration-200 ease-out dark:border-slate-800 ${
                rightOpen
                  ? 'w-full max-h-[min(52vh,420px)] border-t lg:max-h-none lg:w-[min(360px,36vw)] lg:border-l lg:border-t-0'
                  : 'w-0 max-w-0 border-0 lg:max-w-0'
              }`}
            >
              <aside className="flex min-h-0 min-w-[280px] flex-1 flex-col gap-2 overflow-hidden rounded-xl border border-slate-200 bg-white/95 p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900/50 lg:max-h-[calc(100vh-10rem)]">
                <div className="flex shrink-0 items-center justify-between gap-2 lg:hidden">
                  <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Session info</span>
                  <button
                    type="button"
                    className="rounded p-1 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
                    aria-label={rightOpen ? 'Collapse tips' : 'Expand tips'}
                    onClick={() => setRightOpen(!rightOpen)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="shrink-0 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700/90">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 bg-slate-50 px-3 py-2.5 text-left text-sm font-medium text-slate-900 hover:bg-slate-100 dark:bg-slate-800/50 dark:text-slate-100 dark:hover:bg-slate-800/80"
                    aria-expanded={guideAccordionOpen}
                    onClick={() => setGuideAccordionOpen((o) => !o)}
                  >
                    Session guide
                    <ChevronDown
                      className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${guideAccordionOpen ? 'rotate-180' : ''}`}
                      aria-hidden
                    />
                  </button>
                  {guideAccordionOpen ? (
                    <div className="space-y-3 border-t border-slate-200 bg-slate-50/80 px-3 py-3 dark:border-slate-700/80 dark:bg-slate-950/30">
                      <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 dark:border-slate-700/80 dark:bg-slate-950/40">
                        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                          Session timeline
                        </p>
                        <p className="text-xs leading-snug text-slate-700 dark:text-slate-300">
                          We suggest about {guideTargetMinutes} minutes for a comfortable, complete conversation —
                          clear, focused answers help you and the interviewer cover what matters.
                        </p>
                        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                          <div
                            className="h-full rounded-full bg-sky-600 transition-[width] duration-500"
                            style={{ width: `${guideProgressPct}%` }}
                          />
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1.5 tabular-nums">
                          Elapsed {formatElapsed(elapsedSeconds)} · guide ~{guideTargetMinutes} min · minimum submit{' '}
                          {maxLabel}
                        </p>
                      </div>
                      <ul className="space-y-2.5 text-sm text-slate-700 dark:text-slate-300">
                        <li className="flex gap-2">
                          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                          <span>Expect about {guideTargetMinutes} minutes for this round.</span>
                        </li>
                        <li className="flex gap-2">
                          <MessageCircle className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
                          <span>
                            This is a <strong className="font-medium text-slate-900 dark:text-slate-200">guided</strong>{' '}
                            conversation: follow-ups adapt to what you say. Ask for clarification anytime.
                          </span>
                        </li>
                        <li className="flex gap-2">
                          <RefreshCw className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
                          <span>Limited retakes: {retakesRemaining} remaining.</span>
                        </li>
                        <li className="flex gap-2">
                          <Lock className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
                          <span>Your recruiter may review recording and transcript.</span>
                        </li>
                        <li className="flex gap-2">
                          <Calendar className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
                          <span>Language: {languageLabel || '—'}</span>
                        </li>
                      </ul>
                    </div>
                  ) : null}
                </div>

                <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700/90">
                  <button
                    type="button"
                    className="flex w-full shrink-0 items-center justify-between gap-2 bg-slate-50 px-3 py-2.5 text-left text-sm font-medium text-slate-900 hover:bg-slate-100 dark:bg-slate-800/50 dark:text-slate-100 dark:hover:bg-slate-800/80"
                    aria-expanded={transcriptAccordionOpen}
                    onClick={() => setTranscriptAccordionOpen((o) => !o)}
                  >
                    Conversation transcript
                    <ChevronDown
                      className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${transcriptAccordionOpen ? 'rotate-180' : ''}`}
                      aria-hidden
                    />
                  </button>
                  {transcriptAccordionOpen ? (
                    <div
                      className="flex max-h-[min(52vh,360px)] min-h-[120px] flex-1 flex-col border-t border-slate-200 bg-slate-50/80 dark:border-slate-700/80 dark:bg-slate-950/30 lg:max-h-none"
                      aria-busy={sending}
                    >
                      <div className="flex-1 min-h-[80px] overflow-y-auto p-3 space-y-2">
                        {messages.length === 0 && state === 'chat' && (
                          <p className="px-1 py-6 text-center text-xs leading-relaxed text-slate-700 dark:text-slate-300">
                            Use the small <strong className="text-slate-900 dark:text-slate-100">Start conversation</strong>{' '}
                            control under the prompt when you are ready — the interviewer will speak first.
                          </p>
                        )}
                        {messages.map((m, i) => (
                          <div key={`${m.at}-${i}`} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div
                              className={`max-w-[95%] rounded-lg px-3 py-2 text-xs whitespace-pre-wrap ${
                                m.role === 'user'
                                  ? 'bg-sky-600 text-white dark:bg-sky-600'
                                  : 'border border-slate-200 bg-white text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100'
                              }`}
                            >
                              {m.content}
                            </div>
                          </div>
                        ))}
                        <div ref={bottomRef} />
                      </div>
                    </div>
                  ) : null}
                </div>
              </aside>
            </div>
          </div>

          <footer className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur-lg dark:border-slate-800/90 dark:bg-slate-950/85">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              <span className="text-slate-700 dark:text-slate-500">{stepLabel}</span>
              <span className="mx-2 text-slate-300 dark:text-slate-700">·</span>
              <span className="font-mono tabular-nums text-slate-800 sm:hidden dark:text-slate-300">
                {formatElapsed(elapsedSeconds)} / {maxLabel}
              </span>
              <span className="hidden text-xs text-slate-500 sm:inline dark:text-slate-600">Session ID {sessionIdDisplay}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded-full border border-slate-300 p-2 text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-800"
                aria-label="Settings"
              >
                <Settings className="h-4 w-4" />
              </button>
              {state === 'chat' && (
                <button
                  type="button"
                  className="inline-flex min-h-[40px] items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-900 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                  disabled={retakesRemaining < 1 || retakeBusy}
                  onClick={() => setShowRetakeConfirm(true)}
                >
                  Retake
                </button>
              )}
              {state === 'chat' && (
                <button
                  type="button"
                  className="inline-flex min-h-[40px] items-center justify-center rounded-lg border border-slate-400 bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                  disabled={completeBusy}
                  onClick={() => setShowEndConfirm(true)}
                >
                  End interview
                </button>
              )}
            </div>
          </footer>
        </div>
      )}
    </div>
  );
}
